import { create } from 'zustand';

export type SyncStatus = 'IDLE' | 'SYNCING' | 'SUCCESS' | 'WARNING' | 'ERROR';

export const SYNC_SUCCESS_MS = 2500;
export const SYNC_ACTION_SUCCESS_MS = 4500;
export const SYNC_WARNING_MS = 6000;
export const SYNC_ERROR_MS = 8000;
export const SYNC_ALERT_PRIORITY_WINDOW_MS = 400;

export interface SyncAction {
  label: string;
  onClick: () => void;
}

export interface SyncSuccessOptions {
  shortLabel?: string;
  action?: SyncAction | null;
  durationMs?: number;
}

export function deriveShortSyncLabel(message?: string): string {
  if (!message || !message.trim()) {
    return 'Đã lưu';
  }

  const normalized = message.trim();
  const lower = normalized.toLowerCase();

  const codeMatch = normalized.match(/#[A-Z0-9-]{4,}/i);
  if (codeMatch) {
    const code = codeMatch[0].toUpperCase();
    if (lower.includes('gửi') || lower.includes('phản hồi') || lower.includes('góp ý')) {
      return `Đã gửi ${code}`;
    }
    return `Đã lưu ${code}`;
  }

  if (lower.includes('xóa') || lower.includes('xoá') || lower.includes('thùng rác') || lower.includes('hủy phiếu')) {
    return 'Đã xóa';
  }
  if (lower.includes('khôi phục')) {
    return 'Đã khôi phục';
  }
  if (lower.includes('sao chép') || lower.includes('copy')) {
    return 'Đã sao chép';
  }
  if (lower.includes('xuất excel') || lower.includes('tải xuống') || lower.includes('xuất tệp')) {
    return 'Đã xuất tệp';
  }
  if (lower.includes('tải tệp') || lower.includes('tải ảnh') || lower.includes('upload')) {
    return 'Đã tải tệp';
  }
  if (lower.includes('đồng bộ')) {
    return 'Đã đồng bộ';
  }
  // "Gửi duyệt" chưa phải đã duyệt — kiểm trước "duyệt"
  if (lower.includes('gửi duyệt')) {
    return 'Đã gửi duyệt';
  }
  if (lower.includes('duyệt')) {
    return 'Đã duyệt';
  }
  if (lower.includes('chốt')) {
    return 'Đã chốt';
  }
  if (lower.includes('gửi')) {
    return 'Đã gửi';
  }

  return 'Đã lưu';
}

interface SyncState {
  pendingCount: number;
  isSyncing: boolean;
  status: SyncStatus;
  shortLabel: string;
  message: string;
  action: SyncAction | null;
  lastWarning?: string;
  lastError?: string;
  lastSyncedAt?: Date;
  lastAlertAt: number;

  beginTask: (message?: string) => void;
  completeTask: (ok: boolean, message?: string) => void;
  setSyncing: (isSyncing: boolean, message?: string) => void;
  setSuccess: (message?: string, options?: SyncSuccessOptions) => void;
  setWarning: (warning: string) => void;
  setError: (error: string) => void;
  resetStatus: () => void;
}

let revertTimer: ReturnType<typeof setTimeout> | null = null;

function clearRevertTimer() {
  if (revertTimer) {
    clearTimeout(revertTimer);
    revertTimer = null;
  }
}

export const useSyncStore = create<SyncState>((set, get) => ({
  pendingCount: 0,
  isSyncing: false,
  status: 'IDLE',
  shortLabel: '',
  message: '',
  action: null,
  lastWarning: undefined,
  lastError: undefined,
  lastSyncedAt: undefined,
  lastAlertAt: 0,

  beginTask: (message = 'Đang lưu dữ liệu...') => {
    clearRevertTimer();
    set((state) => ({
      pendingCount: state.pendingCount + 1,
      isSyncing: true,
      status: 'SYNCING',
      shortLabel: 'Đang lưu...',
      message,
      action: null,
      lastAlertAt: 0,
    }));
  },

  completeTask: (ok, message) => {
    const state = get();
    const nextPending = Math.max(0, state.pendingCount - 1);

    if (nextPending > 0) {
      set({
        pendingCount: nextPending,
        isSyncing: true,
        status: 'SYNCING',
      });
      return;
    }

    const hasActiveAlert =
      state.status === 'ERROR' ||
      state.status === 'WARNING' ||
      (state.lastAlertAt > 0 && Date.now() - state.lastAlertAt <= SYNC_ALERT_PRIORITY_WINDOW_MS);

    if (hasActiveAlert) {
      set({
        pendingCount: 0,
        isSyncing: false,
      });
      return;
    }

    if (!ok) {
      set({
        pendingCount: 0,
        isSyncing: false,
        status: state.status === 'SYNCING' ? 'IDLE' : state.status,
        shortLabel: state.status === 'SYNCING' ? '' : state.shortLabel,
        message: state.status === 'SYNCING' ? '' : state.message,
      });
      return;
    }

    if (state.status === 'SUCCESS') {
      set({
        pendingCount: 0,
        isSyncing: false,
      });
      return;
    }

    set({ pendingCount: 0, isSyncing: false });
    get().setSuccess(message || 'Đã lưu dữ liệu thành công');
  },

  setSyncing: (isSyncing, message = '') => {
    clearRevertTimer();
    const current = get();
    if (isSyncing) {
      set({
        isSyncing: true,
        status: 'SYNCING',
        shortLabel: 'Đang lưu...',
        message: message || 'Đang xử lý...',
        action: null,
      });
      return;
    }

    if (current.pendingCount > 0) {
      set({
        isSyncing: true,
        status: 'SYNCING',
      });
      return;
    }

    set({
      isSyncing: false,
      status: current.status === 'SYNCING' ? 'IDLE' : current.status,
      shortLabel: current.status === 'SYNCING' ? '' : current.shortLabel,
      message: current.status === 'SYNCING' ? '' : current.message,
    });
  },

  setSuccess: (message = 'Đã lưu thành công', options) => {
    const current = get();
    const hasPriorityAlert =
      !options?.action &&
      (current.status === 'ERROR' || current.status === 'WARNING') &&
      current.lastAlertAt > 0 &&
      Date.now() - current.lastAlertAt <= SYNC_ALERT_PRIORITY_WINDOW_MS;

    if (hasPriorityAlert) {
      return;
    }

    clearRevertTimer();
    const finalMessage = message || 'Đã lưu thành công';
    const nextAction = options?.action ?? null;
    const duration =
      options?.durationMs ?? (nextAction ? SYNC_ACTION_SUCCESS_MS : SYNC_SUCCESS_MS);

    set({
      isSyncing: false,
      status: 'SUCCESS',
      shortLabel: options?.shortLabel || deriveShortSyncLabel(finalMessage),
      message: finalMessage,
      action: nextAction,
      lastSyncedAt: new Date(),
    });

    revertTimer = setTimeout(() => {
      set((state) =>
        state.status === 'SUCCESS'
          ? { status: 'IDLE', shortLabel: '', message: '', action: null }
          : state,
      );
      revertTimer = null;
    }, duration);
  },

  setWarning: (warning: string) => {
    clearRevertTimer();
    set({
      pendingCount: 0,
      isSyncing: false,
      status: 'WARNING',
      shortLabel: 'Cảnh báo',
      lastWarning: warning,
      message: warning,
      action: null,
      lastAlertAt: Date.now(),
    });

    revertTimer = setTimeout(() => {
      set((state) =>
        state.status === 'WARNING'
          ? { status: 'IDLE', shortLabel: '', message: '' }
          : state,
      );
      revertTimer = null;
    }, SYNC_WARNING_MS);
  },

  setError: (error: string) => {
    clearRevertTimer();
    set({
      pendingCount: 0,
      isSyncing: false,
      status: 'ERROR',
      shortLabel: 'Lỗi',
      lastError: error,
      message: error,
      action: null,
      lastAlertAt: Date.now(),
    });

    revertTimer = setTimeout(() => {
      set((state) =>
        state.status === 'ERROR'
          ? { status: 'IDLE', shortLabel: '', message: '' }
          : state,
      );
      revertTimer = null;
    }, SYNC_ERROR_MS);
  },

  resetStatus: () => {
    clearRevertTimer();
    set({
      pendingCount: 0,
      isSyncing: false,
      status: 'IDLE',
      shortLabel: '',
      message: '',
      action: null,
      lastAlertAt: 0,
    });
  },
}));
